import React, { useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import ProgressBar from '@wix/da-ds/pkg/ProgressBar';
import Loader from '@wix/da-ds/pkg/Loader';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { useFileUpload } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { IMAGE_MIME, VIDEO_MIME } from '@wix/da-shared-react/pkg/utils/mime';
import { UploadType } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { useSubmitFormStatus } from '../../../hooks/useSubmitFormStatus';
import { SubmitFormContext } from '../../../context';
import ImagePreview from '../ImagePreview';

import s from './SingleImagePreview.scss';

interface Props {
  className?: string;
}

const SingleImagePreview: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { initResponse, params, refreshInitData } =
    useContext(SubmitFormContext)!;
  const { isFormBusy, isMainUploading } = useSubmitFormStatus();
  const isVideo = initResponse?.deviation?.isVideo;

  const onSuccess = useCallback(() => {
    refreshInitData && refreshInitData();
  }, [refreshInitData]);

  const onFailure = useCallback(
    (payload, error) => {
      dispatch(addErrorNotification(error.message));
    },
    [dispatch]
  );

  const allowUpload = !isFormBusy && !isMainUploading;
  const handleDrop = useCallback(() => {
    return true;
  }, []);

  const { input, activeUploads, getDropzoneRootProps } = useFileUpload({
    id: 'submit-image-replacement',
    uploadType: UploadType.DEVIATION_REPLACE_ORIGINAL,
    uploadParams: {
      deviationId: params?.deviationId,
    },
    allowMultiple: false,
    allowDragAndDrop: false,
    acceptedMimeTypes: isVideo ? VIDEO_MIME : IMAGE_MIME,
    onDrop: handleDrop,
    onSuccess,
    onFailure,
  });

  const activeUpload = [...activeUploads].pop();
  const uploadProgress = activeUpload?.progress;

  return (
    <div className={cn(s['root'], className)}>
      <div
        className={s['edit-btn-wrapper']}
        {...(allowUpload ? getDropzoneRootProps() : {})}
      >
        <IconButton
          className={cn(s['edit-btn'], !allowUpload && s['disabled'])}
          size={'small'}
          icon={EditIcon}
          disabled={!allowUpload}
          tooltipOffset={[0, 8]}
          tooltipText={
            allowUpload
              ? t('deviationSubmit.form.imageReplace.tooltip.text')
              : undefined
          }
          aria-label={t('deviationSubmit.form.imageReplace.tooltip.text')}
        />
        {input}
      </div>

      {isMainUploading && (
        <div className={s['uploading']}>
          <Loader size="big" state="eating" />
          {uploadProgress && (
            <ProgressBar
              progress={uploadProgress}
              className={s['progress-bar']}
            />
          )}
          <div className={s['progress-title']}>
            {t('uploadZone.progress.title')}
          </div>
        </div>
      )}
      {allowUpload && (
        <ImagePreview
          className={s['preview']}
          deviation={initResponse?.deviation}
        />
      )}
    </div>
  );
};
SingleImagePreview.displayName = 'SingleImagePreview';

export default SingleImagePreview;
